import React, { useCallback, useEffect, useRef, useState } from "react";
import { predictImage } from "./api/predict";
import "./index.css";

function App() {
    const [imageUrl, setImageUrl] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hover, setHover] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const runPrediction = useCallback(async (input) => {
        setError(null);
        setResult(null);
        try {
            setLoading(true);
            const prediction = await predictImage(input);
            setResult(prediction);
        } catch (err) {
            setError(err && err.message ? err.message : "Prediction failed.");
        } finally {
            setLoading(false);
        }
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        const trimmed = imageUrl.trim();
        if (!trimmed) {
            setError("Enter an image URL or drop a file below.");
            return;
        }
        await runPrediction(trimmed);
    }

    function handleFiles(fileList) {
        if (!fileList || fileList.length === 0) return;
        const file = fileList[0];
        if (!file.type.startsWith("image/")) {
            setError("That file isn't an image.");
            return;
        }
        setImageUrl("");
        runPrediction(file);
    }

    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    }

    useEffect(() => {
        function onPaste(e) {
            const items = e.clipboardData && e.clipboardData.items;
            if (!items) return;
            for (const item of items) {
                if (item.kind === "file" && item.type.startsWith("image/")) {
                    const file = item.getAsFile();
                    if (file) {
                        setImageUrl("");
                        runPrediction(file);
                        e.preventDefault();
                        return;
                    }
                }
            }
        }
        document.addEventListener("paste", onPaste);
        return () => document.removeEventListener("paste", onPaste);
    }, [runPrediction]);

    const verdictClass =
        result && result.verdict === "AI-generated"
            ? "verdict-chip verdict-chip-ai"
            : "verdict-chip verdict-chip-real";

    const probabilityPct = result ? (result.probability * 100).toFixed(1) : null;

    return (
        <div className="app-root">
            <div className="panel">
                <header className="panel-header">
                    <h1 className="panel-title">Image Authenticity</h1>
                    <p className="panel-subtitle">
                        Paste a URL, drop an image, or paste from clipboard.
                    </p>
                </header>

                <form className="control-row" onSubmit={handleSubmit}>
                    <input
                        type="url"
                        placeholder="https://example.com/image.jpg"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        disabled={loading}
                    />
                    <button type="submit" disabled={loading}>
                        {loading ? "Analyzing…" : "Run"}
                    </button>
                </form>

                <div
                    className={`dropzone${dragOver ? " dropzone-active" : ""}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    role="button"
                    tabIndex={0}
                >
                    <span>Drop an image, click to choose, or ⌘V to paste</span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFiles(e.target.files)}
                    />
                </div>

                {error && <div className="inline-error">{error}</div>}

                {result && (
                    <div className="result-row">
                        <div
                            className="result-image"
                            onMouseLeave={() => setHover(false)}
                            onMouseEnter={() => setHover(true)}
                        >
                            <img
                                src={hover && result.overlayUrl ? result.overlayUrl : result.imageUrl}
                                alt="Prediction"
                            />
                            <div className={verdictClass}>{result.verdict}</div>
                        </div>
                        <div className="result-detail">
                            <strong>{probabilityPct}%</strong> probability AI-generated
                            {result.overlayUrl && (
                                <span className="muted"> · hover for Grad-CAM</span>
                            )}
                        </div>
                    </div>
                )}

                <footer className="panel-footer">
                    <span>DINOv3 · CS229</span>
                </footer>
            </div>
        </div>
    );
}

export default App;
